import { useEffect, useMemo, useState } from "react";
import { createCoupon, deleteCoupon, fetchCoupons, fetchUsers, updateCoupon } from "../api";

const EMPTY_FORM = {
    code: "",
    discountAmount: "",
    discountType: "fixed",
    maxUses: "",
    expiresAt: "",
    applicableBookIds: [],
    applicableUserId: "",
};

export default function AdminCouponManager({ user, books }) {
    const [coupons, setCoupons] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [deletingCouponId, setDeletingCouponId] = useState(null);
    const [error, setError] = useState("");
    const [editingCouponId, setEditingCouponId] = useState(null);
    const [form, setForm] = useState({ ...EMPTY_FORM });

    const sortedBooks = useMemo(
        () => [...books].sort((a, b) => (a.title || "").localeCompare(b.title || "")),
        [books]
    );


    const sortedUsers = useMemo(
        () => [...users].sort((a, b) => (a.name || a.email || "").localeCompare(b.name || b.email || "")),
        [users]
    );

    const loadCoupons = async () => {
        setLoading(true);
        setError("");
        try {
            const [couponData, userData] = await Promise.all([
                fetchCoupons(user.token),
                fetchUsers(user.token),
            ]);
            setCoupons(couponData);
            setUsers(userData);
        } catch (err) {
            setError(err.message || "Failed to load coupon data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadCoupons();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user.token]);

    const handleChange = (field) => (event) => {
        setForm((current) => ({
            ...current,
            [field]: event.target.value,
        }));
    };

    const handleBookToggle = (bookId) => {
        setForm((current) => {
            const ids = current.applicableBookIds;
            const strId = String(bookId);
            if (ids.includes(strId)) {
                return { ...current, applicableBookIds: ids.filter((id) => id !== strId) };
            }
            return { ...current, applicableBookIds: [...ids, strId] };
        });
    };

    const handleEdit = (coupon) => {
        setEditingCouponId(coupon.id);
        setError("");

        const expiresLocal = coupon.expiresAt
            ? new Date(coupon.expiresAt).toISOString().slice(0, 16)
            : "";

        setForm({
            code: coupon.code || "",
            discountAmount: String(coupon.discountAmount || ""),
            discountType: coupon.discountType || "fixed",
            maxUses: String(coupon.maxUses || ""),
            expiresAt: expiresLocal,
            applicableBookIds: Array.isArray(coupon.applicableBookIds)
                ? coupon.applicableBookIds.map(String)
                : [],
            applicableUserId: coupon.applicableUserId || "",
        });
    };

    const handleCancelEdit = () => {
        setEditingCouponId(null);
        setForm({ ...EMPTY_FORM });
        setError("");
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSubmitting(true);
        setError("");

        try {
            const payload = {
                code: form.code.trim().toUpperCase(),
                discountAmount: Number(form.discountAmount),
                discountType: form.discountType,
                maxUses: Number(form.maxUses),
                expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : "",
                applicableBookIds: form.applicableBookIds,
                applicableUserId: form.applicableUserId,
            };

            if (editingCouponId) {
                await updateCoupon(user.token, editingCouponId, payload);
            } else {
                await createCoupon(user.token, payload);
            }

            setForm({ ...EMPTY_FORM });
            setEditingCouponId(null);
            await loadCoupons();
        } catch (err) {
            setError(err.message || `Failed to ${editingCouponId ? "update" : "create"} coupon.`);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteCoupon = async (couponId) => {
        setError("");
        try {
            await deleteCoupon(user.token, couponId);
            setCoupons((current) => current.filter((coupon) => String(coupon.id) !== String(couponId)));
            if (editingCouponId === couponId) {
                handleCancelEdit();
            }
        } catch (err) {
            setError(err.message || "Failed to delete coupon.");
        } finally {
            setDeletingCouponId(null);
        }
    };

    const fmtDate = (value) => {
        if (!value) return "No expiry";
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? "Invalid date" : date.toLocaleString();
    };

    const shorten = (value, len = 40) => {
        if (!value) return "";
        return value.length > len ? `${value.slice(0, len)}...` : value;
    };

    const fmtDiscount = (coupon) => {
        if (coupon.discountType === "percentage") {
            return `${Number(coupon.discountAmount || 0)}% off`;
        }
        return `Rs. ${Number(coupon.discountAmount || 0).toFixed(2)} off`;
    };

    const resolveBookTitles = (coupon) => {
        const ids = Array.isArray(coupon.applicableBookIds) ? coupon.applicableBookIds : [];
        if (ids.length === 0) return "All books";
        return ids.map((id) => {
            const book = books.find((b) => String(b._id) === String(id));
            return book ? shorten(book.title, 30) : `ID ${id}`;
        }).join(", ");
    };

    const selectStyle = {
        padding: "12px",
        borderRadius: 10,
        border: "1px solid var(--border)",
        background: "white",
        fontFamily: "inherit",
        fontSize: "0.9rem",
        width: "100%",
    };

    return (
        <>
            <style>{`
                @media (max-width: 900px) {
                    .admin-coupon-grid {
                        grid-template-columns: 1fr !important;
                    }
                }
            `}</style>
            <div className="admin-coupon-grid" style={{ display: "grid", gridTemplateColumns: "minmax(320px, 460px) 1fr", gap: 24 }}>
                <div style={{ background: "white", border: "1px solid var(--border)", borderRadius: 16, padding: 24 }}>
                    <h3 style={{ marginTop: 0, marginBottom: 8, color: "var(--forest)" }}>
                        {editingCouponId ? "Edit Coupon" : "Create Coupon"}
                    </h3>
                    <p style={{ marginTop: 0, marginBottom: 20, color: "var(--text-light)", fontSize: "0.95rem" }}>
                        {editingCouponId
                            ? "Update this coupon's details. Leave book selection empty for all books."
                            : "Leave book or user blank to make the coupon available across that scope."}
                    </p>

                    <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                        <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            <span style={{ fontWeight: 600 }}>Coupon Code</span>
                            <input type="text" value={form.code} onChange={handleChange("code")} placeholder="SAVE500" style={{ padding: "12px 14px", borderRadius: 10, border: "1px solid var(--border)", fontFamily: "inherit" }} required />
                        </label>

                        <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            <span style={{ fontWeight: 600 }}>Discount Type</span>
                            <div style={{ display: "flex", gap: 8 }}>
                                {[
                                    { value: "fixed", label: "Fixed (Rs.)" },
                                    { value: "percentage", label: "Percentage (%)" },
                                ].map((opt) => (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => setForm((c) => ({ ...c, discountType: opt.value }))}
                                        style={{
                                            flex: 1,
                                            padding: "10px 14px",
                                            borderRadius: 10,
                                            border: form.discountType === opt.value
                                                ? "2px solid var(--forest)"
                                                : "1px solid var(--border)",
                                            background: form.discountType === opt.value ? "#e8f5e9" : "white",
                                            color: form.discountType === opt.value ? "var(--forest)" : "var(--text-light)",
                                            fontWeight: 700,
                                            cursor: "pointer",
                                            fontFamily: "inherit",
                                            fontSize: "0.9rem",
                                            transition: "all 0.15s ease",
                                        }}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </label>

                        <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            <span style={{ fontWeight: 600 }}>
                                {form.discountType === "percentage" ? "Discount Percentage" : "Discount Amount"}
                            </span>
                            <input
                                type="number"
                                min="0.01"
                                max={form.discountType === "percentage" ? "100" : undefined}
                                step="0.01"
                                value={form.discountAmount}
                                onChange={handleChange("discountAmount")}
                                placeholder={form.discountType === "percentage" ? "10" : "250"}
                                style={{ padding: "12px 14px", borderRadius: 10, border: "1px solid var(--border)", fontFamily: "inherit" }}
                                required
                            />
                            {form.discountType === "percentage" && (
                                <span style={{ fontSize: "0.82rem", color: "var(--text-light)" }}>Enter a value between 1 and 100</span>
                            )}
                        </label>

                        <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            <span style={{ fontWeight: 600 }}>Usage Limit</span>
                            <input type="number" min="1" step="1" value={form.maxUses} onChange={handleChange("maxUses")} placeholder="10" style={{ padding: "12px 14px", borderRadius: 10, border: "1px solid var(--border)", fontFamily: "inherit" }} required />
                        </label>

                        <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            <span style={{ fontWeight: 600 }}>Expiry Date</span>
                            <input type="datetime-local" value={form.expiresAt} onChange={handleChange("expiresAt")} style={{ padding: "12px 14px", borderRadius: 10, border: "1px solid var(--border)", fontFamily: "inherit" }} />
                        </label>

                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            <span style={{ fontWeight: 600 }}>
                                Applicable Books
                                {form.applicableBookIds.length > 0 && (
                                    <span style={{ fontWeight: 400, color: "var(--text-light)", marginLeft: 8, fontSize: "0.85rem" }}>
                                        ({form.applicableBookIds.length} selected)
                                    </span>
                                )}
                            </span>
                            <select
                                value=""
                                onChange={(e) => {
                                    if (e.target.value) {
                                        handleBookToggle(e.target.value);
                                    }
                                }}
                                style={selectStyle}
                            >
                                <option value="">Select a book to add…</option>
                                {sortedBooks
                                    .filter((book) => book.title && !form.applicableBookIds.includes(String(book._id)))
                                    .map((book) => (
                                        <option key={book._id} value={book._id}>
                                            {shorten(book.title, 50)}
                                        </option>
                                    ))}
                            </select>
                            {form.applicableBookIds.length > 0 && (
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
                                    {form.applicableBookIds.map((id) => {
                                        const book = books.find((b) => String(b._id) === String(id));
                                        const label = book?.title ? shorten(book.title, 30) : `ID ${id}`;
                                        return (
                                            <span
                                                key={id}
                                                style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: 6,
                                                    background: "#e8f5e9",
                                                    color: "var(--forest)",
                                                    borderRadius: 8,
                                                    padding: "5px 10px",
                                                    fontSize: "0.82rem",
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {label}
                                                <button
                                                    type="button"
                                                    onClick={() => handleBookToggle(id)}
                                                    style={{
                                                        border: "none",
                                                        background: "transparent",
                                                        color: "var(--forest)",
                                                        cursor: "pointer",
                                                        padding: 0,
                                                        fontSize: "1rem",
                                                        lineHeight: 1,
                                                        fontWeight: 700,
                                                    }}
                                                    title="Remove"
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        );
                                    })}
                                    <button
                                        type="button"
                                        onClick={() => setForm((c) => ({ ...c, applicableBookIds: [] }))}
                                        style={{
                                            padding: "4px 10px",
                                            borderRadius: 6,
                                            border: "1px solid var(--border)",
                                            background: "white",
                                            color: "var(--text-light)",
                                            cursor: "pointer",
                                            fontFamily: "inherit",
                                            fontSize: "0.78rem",
                                        }}
                                    >
                                        Clear all
                                    </button>
                                </div>
                            )}
                            {form.applicableBookIds.length === 0 && (
                                <span style={{ fontSize: "0.82rem", color: "var(--text-light)" }}>
                                    No books selected — coupon applies to all books
                                </span>
                            )}
                        </div>

                        <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            <span style={{ fontWeight: 600 }}>Applicable User</span>
                            <select value={form.applicableUserId} onChange={handleChange("applicableUserId")} style={selectStyle}>
                                <option value="">All users</option>
                                {sortedUsers.map((couponUser) => (
                                    <option key={couponUser._id || couponUser.id} value={couponUser._id || couponUser.id}>
                                        {shorten(`${couponUser.name || "User"} (${couponUser.email || "no email"})`, 48)}
                                    </option>
                                ))}
                            </select>
                        </label>

                        {error ? (
                            <div style={{ color: "#c62828", fontSize: "0.9rem", background: "#fff3f3", border: "1px solid #f3c7c7", borderRadius: 10, padding: 12 }}>
                                {error}
                            </div>
                        ) : null}

                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                            <button type="submit" className="btn btn-forest" disabled={submitting} style={{ justifyContent: "center", opacity: submitting ? 0.7 : 1, flex: 1 }}>
                                {submitting
                                    ? (editingCouponId ? "Updating..." : "Creating...")
                                    : (editingCouponId ? "Update Coupon" : "Create Coupon")}
                            </button>
                            {editingCouponId && (
                                <button
                                    type="button"
                                    onClick={handleCancelEdit}
                                    className="btn btn-outline"
                                    style={{ flex: "0 0 auto" }}
                                >
                                    Cancel
                                </button>
                            )}
                        </div>
                    </form>
                </div>

                <div style={{ background: "white", border: "1px solid var(--border)", borderRadius: 16, padding: 24 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 20 }}>
                        <div>
                            <h3 style={{ margin: 0, color: "var(--forest)" }}>Existing Coupons</h3>
                            <p style={{ margin: "6px 0 0", color: "var(--text-light)", fontSize: "0.95rem" }}>
                                {coupons.length} coupon{coupons.length === 1 ? "" : "s"} configured
                            </p>
                        </div>
                        <button className="btn btn-outline" onClick={loadCoupons} disabled={loading}>
                            {loading ? "Refreshing..." : "Refresh"}
                        </button>
                    </div>

                    {loading ? (
                        <p style={{ color: "var(--text-light)" }}>Loading coupons...</p>
                    ) : coupons.length === 0 ? (
                        <p style={{ color: "var(--text-light)" }}>No coupons created yet.</p>
                    ) : (
                        <div style={{ display: "grid", gap: 14 }}>
                            {coupons.map((coupon) => {
                                const remainingUses = Math.max(0, Number(coupon.maxUses || 0) - Number(coupon.usesCount || 0));
                                const isExpired = coupon.expiresAt ? new Date(coupon.expiresAt).getTime() <= Date.now() : false;
                                const isEditing = editingCouponId === coupon.id;

                                return (
                                    <div key={coupon.id} style={{
                                        border: isEditing ? "2px solid var(--forest)" : "1px solid var(--border)",
                                        borderRadius: 14,
                                        padding: 18,
                                        background: isEditing ? "#f1f8e9" : isExpired ? "#fff7f7" : "var(--paper)",
                                        transition: "all 0.2s ease",
                                    }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
                                            <div>
                                                <div style={{ fontSize: "1rem", fontWeight: 800, color: "var(--forest)" }}>{coupon.code}</div>
                                                <div style={{ marginTop: 4, color: "var(--text-light)", fontSize: "0.9rem" }}>
                                                    Books: {resolveBookTitles(coupon)}
                                                </div>
                                                <div style={{ marginTop: 4, color: "var(--text-light)", fontSize: "0.9rem" }}>
                                                    User: {coupon.applicableUserId ? (coupon.applicableUserName || coupon.applicableUserEmail || coupon.applicableUserId) : "All users"}
                                                </div>
                                            </div>
                                            <div style={{ textAlign: "right" }}>
                                                <div style={{ fontWeight: 700 }}>
                                                    {fmtDiscount(coupon)}
                                                </div>
                                                <div style={{
                                                    display: "inline-block",
                                                    marginTop: 4,
                                                    padding: "2px 8px",
                                                    borderRadius: 6,
                                                    fontSize: "0.78rem",
                                                    fontWeight: 600,
                                                    background: coupon.discountType === "percentage" ? "#e3f2fd" : "#f3e5f5",
                                                    color: coupon.discountType === "percentage" ? "#1565c0" : "#7b1fa2",
                                                }}>
                                                    {coupon.discountType === "percentage" ? "Percentage" : "Fixed"}
                                                </div>
                                                <div style={{ color: isExpired ? "#c62828" : "var(--text-light)", fontSize: "0.9rem", marginTop: 4 }}>
                                                    {isExpired ? "Expired" : fmtDate(coupon.expiresAt)}
                                                </div>
                                            </div>
                                        </div>

                                        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 14, color: "var(--text-light)", fontSize: "0.9rem" }}>
                                            <span>Used: {coupon.usesCount}/{coupon.maxUses}</span>
                                            <span>Remaining: {remainingUses}</span>
                                        </div>

                                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
                                            {deletingCouponId === coupon.id ? (
                                                <div style={{ display: "flex", gap: 8 }}>
                                                    <button onClick={() => handleDeleteCoupon(coupon.id)} style={{ border: "none", background: "#c62828", color: "white", borderRadius: 8, padding: "8px 12px", cursor: "pointer", fontWeight: 700 }}>
                                                        Confirm Delete
                                                    </button>
                                                    <button onClick={() => setDeletingCouponId(null)} style={{ border: "1px solid var(--border)", background: "white", color: "var(--text-light)", borderRadius: 8, padding: "8px 12px", cursor: "pointer", fontWeight: 700 }}>
                                                        Cancel
                                                    </button>
                                                </div>
                                            ) : (
                                                <>
                                                    <button
                                                        onClick={() => handleEdit(coupon)}
                                                        style={{
                                                            border: "1px solid #c8d6e5",
                                                            background: isEditing ? "var(--forest)" : "#f0f4f8",
                                                            color: isEditing ? "white" : "#2c3e50",
                                                            borderRadius: 8,
                                                            padding: "8px 12px",
                                                            cursor: "pointer",
                                                            fontWeight: 700,
                                                            transition: "all 0.15s ease",
                                                        }}
                                                    >
                                                        {isEditing ? "Editing..." : "Edit"}
                                                    </button>
                                                    <button onClick={() => setDeletingCouponId(coupon.id)} style={{ border: "1px solid #efc1c1", background: "#fff7f7", color: "#c62828", borderRadius: 8, padding: "8px 12px", cursor: "pointer", fontWeight: 700 }}>
                                                        Delete
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
